// src/hooks/useSampleOrderCart.js
import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Shared “shopping cart” for building a sample order from the catalog.
 * ✅ Persists to localStorage
 * ✅ Scoped by companyId + userId (so no collisions)
 * Stores items as a map: { [productId]: { productId, quantity } }
 */
export default function useSampleOrderCart({ companyId, currentUserId }) {
  const storageKey = useMemo(() => {
    const c = companyId || "no_company";
    const u = currentUserId || "no_user";
    return `sampleOrderCart:${c}:${u}`;
  }, [companyId, currentUserId]);

  const canUseStorage = typeof window !== "undefined" && !!window.localStorage;

  const [cartMap, setCartMap] = useState({}); // { [productId]: { productId, quantity } }

  // Load cart (per storageKey)
  useEffect(() => {
    if (!canUseStorage) return;

    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) {
        setCartMap({});
        return;
      }
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") setCartMap(parsed);
      else setCartMap({});
    } catch (err) {
      console.warn("[useSampleOrderCart] Failed to load cart:", err);
      setCartMap({});
    }
  }, [storageKey, canUseStorage]);

  // Persist cart
  useEffect(() => {
    if (!canUseStorage) return;

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(cartMap || {}));
    } catch (err) {
      console.warn("[useSampleOrderCart] Failed to save cart:", err);
    }
  }, [storageKey, cartMap, canUseStorage]);

  // Optional: if another browser tab updates this cartKey, sync it here
  useEffect(() => {
    if (!canUseStorage) return;

    const onStorage = (e) => {
      if (!e) return;
      if (e.key !== storageKey) return;
      try {
        const parsed = e.newValue ? JSON.parse(e.newValue) : {};
        setCartMap(parsed && typeof parsed === "object" ? parsed : {});
      } catch {
        setCartMap({});
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey, canUseStorage]);

  const itemsArray = useMemo(() => Object.values(cartMap || {}), [cartMap]);

  const distinctCount = itemsArray.length;

  const totalUnits = useMemo(() => {
    return itemsArray.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
  }, [itemsArray]);

  const getQty = useCallback(
    (productId) => Number(cartMap?.[productId]?.quantity) || 0,
    [cartMap]
  );

  const setQty = useCallback((productId, qty) => {
    const safeQty = Math.max(0, Number(qty) || 0);

    setCartMap((prev) => {
      const next = { ...(prev || {}) };
      if (!productId) return next;

      if (safeQty <= 0) {
        delete next[productId];
        return next;
      }

      next[productId] = { productId, quantity: safeQty };
      return next;
    });
  }, []);

  const add = useCallback((productId, delta = 1) => {
    const d = Number(delta) || 1;

    setCartMap((prev) => {
      const next = { ...(prev || {}) };
      if (!productId) return next;

      const currentQty = Number(next?.[productId]?.quantity) || 0;
      const newQty = Math.max(1, currentQty + d);

      next[productId] = { productId, quantity: newQty };
      return next;
    });
  }, []);

  const remove = useCallback((productId) => {
    setCartMap((prev) => {
      const next = { ...(prev || {}) };
      delete next[productId];
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setCartMap({});
  }, []);

  /**
   * Replace the entire cart from:
   * - an array: [{productId, quantity}, ...]
   * - OR a map: { [productId]: {productId, quantity} }
   */
  const replace = useCallback((itemsOrMap) => {
    // array -> map
    if (Array.isArray(itemsOrMap)) {
      const next = {};
      for (const it of itemsOrMap) {
        const pid = (it?.productId || "").toString().trim();
        const qty = Math.max(0, Number(it?.quantity) || 0);
        if (pid && qty > 0) next[pid] = { productId: pid, quantity: qty };
      }
      setCartMap(next);
      return;
    }

    // map -> sanitize
    if (itemsOrMap && typeof itemsOrMap === "object") {
      const next = {};
      for (const [pidRaw, v] of Object.entries(itemsOrMap)) {
        const pid = (v?.productId || pidRaw || "").toString().trim();
        const qty = Math.max(0, Number(v?.quantity) || 0);
        if (pid && qty > 0) next[pid] = { productId: pid, quantity: qty };
      }
      setCartMap(next);
      return;
    }

    setCartMap({});
  }, []);

  return {
    storageKey,
    cartMap,
    itemsArray,
    distinctCount,
    totalUnits,
    getQty,
    setQty,
    add,
    remove,
    clear,
    replace,
  };
}
